import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../apiBase";
import { Link } from "react-router-dom";
import { ownerSecretHeader, getOwnerSecret } from "../ownerSecret";

function HomePage({ setCurrentAircraft }) {

  const navigate = useNavigate();
  const [aircraftList, setAircraftList] = useState([]);
  const [err, setErr] = useState("");
  const [engineDrafts, setEngineDrafts] = useState({});

  const loadAircraft = async () => {
    setErr("");
    try {
      const res = await fetch(`${API_BASE}/api/aircraft`);

      if (!res.ok) throw new Error(`GET /api/aircraft failed: ${res.status}`);
      const data = await res.json();

      const arr = Array.isArray(data) ? data : (data.aircraft || []);
      setAircraftList(arr);
    } catch (e) {
      setErr(String(e?.message || e));
    }
  };

  useEffect(() => {
    loadAircraft();
  }, []);

  const chooseAircraft = (plane) => {
    const tail = plane.tailNumber || plane.id || "";
    const engineValue = engineDrafts[tail] ?? plane.engine ?? "";

    setCurrentAircraft({
      ...plane,
      tailNumber: tail,
      makeModel: plane.makeModel || "",
      engine: engineValue,
      imageUrl: plane.imageUrl || plane.thumbUrl || "",
    });
  };

  async function handleAirplanePictureChange(e, airplaneId) {
    const file = e.target.files[0];

    if (!file) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("airplaneId", airplaneId);

      const res = await fetch(`${API_BASE}/api/upload-picture`, {
        method: "POST",
        headers: ownerSecretHeader(airplaneId),
        body: formData,
      });

      if (!res.ok) {
        if (res.status === 403) {
          alert("You're not authorized to change this aircraft's picture.");
        } else {
          alert("Picture upload failed");
        }
        return;
      }

      await res.json();
      await loadAircraft();

    } catch (err) {
      console.error(err);
      alert("Error uploading picture");
    }
  }

  async function handleDeleteAirplane(airplaneId) {
    if (!window.confirm(`Delete airplane ${airplaneId}?`)) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/aircraft`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...ownerSecretHeader(airplaneId),
        },
        body: JSON.stringify({ tailNumber: airplaneId }),
      });

      if (!res.ok) {
        if (res.status === 403) {
          alert("You're not authorized to delete this aircraft.");
        } else {
          alert("Delete airplane failed");
        }
        return;
      }

      await loadAircraft();
    } catch (err) {
      console.error(err);
      alert("Error deleting airplane");
    }
  }

  async function handleEngineAutosave(airplaneId, engineValue) {
    try {
      const res = await fetch(`${API_BASE}/api/aircraft`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...ownerSecretHeader(airplaneId),
        },
        body: JSON.stringify({
          tailNumber: airplaneId,
          engine: engineValue,
        }),
      });

      if (!res.ok) {
        console.error("Engine autosave failed", res.status);
      }
    } catch (err) {
      console.error("Error autosaving engine", err);
    }
  }

  return (
    <article className="App-article">
      <h2 style={{fontSize: "38px"}}>Takeoff and Landing Data-Card</h2>

      <section className="home-hero">
        <h1>Free Aviation Preflight Planning Tool</h1>
        <p>
          Use these tools to support preflight planning, and aeronautical
          decision-making before every flight. <br></br>
          Note: This tool must not be used as a primary source for navigation, performance
          calculations, or safety-critical decisions.
          Pilots must consult official FAA publications, approved flight planning tools,
          aircraft operating handbooks (POH/AFM), and current weather briefings
          before conducting any flight.
        </p>
      </section>

      <div className="tool-content">
        <h2>Related Tools</h2>
        <p>
          <Link to="/density-altitude">Density Altitude</Link> |
          <Link to="/cw">Crosswind/Headwind</Link> |
          <Link to="/mnvr">Maneuvers</Link> |
          <Link to="/airframe-powerplant">Airframe & Powerplant</Link> |
          <Link to="/airplane-prebuy">Airplane Prebuy Checklist</Link> |
          <Link to="/adjustable-amortization">Aircraft Loan</Link>
        </p>
      </div>

      <div style={{ marginBottom: "12px" }}>
        <button onClick={() => navigate("/aircraft/create")}>
          Create Airplane
        </button>
      </div>

      {err && <div className="error">{err}</div>}

      <div className="aircraft-grid">
        {aircraftList.map((p) => (
          <div
            key={p.tailNumber}
            className="aircraft-card"
            style={{ position: "relative" }}
          >
            {!!getOwnerSecret(p.tailNumber || p.id) && (
              <button
                className="delete-airplane-btn"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();

                  const airplaneId = p.tailNumber || p.id;
                  if (!airplaneId) {
                    alert("Missing airplane ID");
                    return;
                  }
                  handleDeleteAirplane(airplaneId);
                }}
              >
                ×
              </button>
            )}

            <button
              className="aircraft-select-btn"
              onClick={() => chooseAircraft(p)}
              title={`Select ${p.tailNumber}`}
              type="button"
            >
              <img
                className="aircraft-img"
                src={p.thumbUrl || p.imageUrl || ""}
                alt={p.tailNumber || ""}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />

              <div className="aircraft-text">
                <div className="aircraft-tail">{p.tailNumber}</div>
                <div className="aircraft-model">{p.makeModel}</div>
                <div className="aircraft-engine">
                  <input
                    className="aircraft-engine-input"
                    type="text"
                    value={engineDrafts[p.tailNumber] ?? p.engine ?? ""}
                    placeholder="Engine"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const value = e.target.value;

                      setEngineDrafts((prev) => ({
                        ...prev,
                        [p.tailNumber]: value,
                      }));

                      handleEngineAutosave(p.tailNumber, value);
                    }}
                  />
                </div>
              </div>
            </button>

            <div
              style={{
                marginTop: "8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <label
                style={{
                  padding: "10px 16px",
                  background: "linear-gradient(to bottom, #ffffff, #d9d9d9)",
                  border: "2px solid #444",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: "bold",
                  color: "darkblue",
                  textAlign: "center",
                  boxShadow: "0 3px 6px rgba(0,0,0,0.35)",
                  display: "inline-block",
                  transition: "all 0.15s ease",
                }}
              >
                Change Picture
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: "none" }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => handleAirplanePictureChange(e, p.tailNumber)}
                />
              </label>

              <div style={{ fontSize: "12px", color: "#222", textAlign: "center" }}>
                Click airplane image to select. <br />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div>
        <p style={{fontSize: "28px"}}>
          Create Airplane or Select Airplane by clicking on the airplane image.
        </p>
      </div>

      <section className="home-info-section">
        <h2>Preflight Planning and Calculation Tools </h2>
        <p className="purpose-info-text">
          Preflight planning is required under 14 CFR § 91.103, which requires
          each pilot in command, before beginning a flight, to become familiar
          with all available information concerning that flight.
          <br />
          This application is designed to support common preflight planning and calculations,
          including Vspeed information, Ground Roll, density altitude, wind correction, navigation planning,
          and weight & balance analysis.
        </p>
      </section>

      <div className="ad-banner">
        Ad Space
      </div>

      <div className="tool-content">
        <h2>Related Tools</h2>
        <p>
          <Link to="/vsps">Vspeed</Link> |
          <Link to="/rollout">Ground Roll</Link> |
          <Link to="/navlog">NavLog</Link> |
          <Link to="/wtbals">Weight & Balance</Link> |
          <Link to="/density-altitude">Density Altitude</Link> |
          <Link to="/cw">Crosswind/Headwind</Link> |
          <Link to="/mnvr">Maneuvers</Link> |
          <Link to="/airframe-powerplant">Airframe & Powerplant</Link> |
          <Link to="/airplane-prebuy">Airplane Prebuy Checklist</Link> |
          <Link to="/adjustable-amortization">Aircraft Loan</Link>
        </p>
      </div>
    </article>
  );
}

export default HomePage;